import { useEffect } from 'react';
import { ExternalLink, Info, KeyRound, X } from 'lucide-react';
import { SocialPlatform } from '@mg/shared';
import { cn } from '../lib/cn';
import { FacebookIcon } from '../features/social/brand-icons';
import { GuideByoaNote } from '../features/social/GuideByoaNote';
import { PLATFORM_META } from '../features/social/platforms';
import { useOAuthRedirectUris } from '../features/social/useOAuthRedirectUris';

/** A Meta permission, as the guide names it. */
function scope(name: string) {
  return <code className="rounded bg-chip px-1 text-xs">{name}</code>;
}

/**
 * Standalone "Get your Facebook App ID & Secret" guide, opened in a new tab from
 * Site Integrations. Walks a non-developer through creating a Meta App, copying
 * the App ID + App Secret, registering the redirect URI, and adding permissions.
 * Renders OUTSIDE the agency nav shell and needs no auth.
 */
export function FacebookAppGuide() {
  useEffect(() => {
    const prev = document.title;
    document.title = 'Get your Facebook App ID & Secret · Smorgas Social';
    return () => {
      document.title = prev;
    };
  }, []);
  // The OAuth redirect URI the install's Meta App must register, as the server sends it.
  const redirectUri = useOAuthRedirectUris()?.facebook ?? null;

  const steps: { title: string; body: React.ReactNode }[] = [
    {
      title: 'Open the Meta for Developers site',
      body: (
        <>
          Go to{' '}
          <a
            href="https://developers.facebook.com/apps"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 font-medium text-link hover:text-action-hover"
          >
            developers.facebook.com/apps <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </a>{' '}
          and log in with the Facebook account that manages your Page. If it&apos;s your first time,
          accept the developer terms.
        </>
      ),
    },
    {
      title: 'Create a new app',
      body: (
        <>
          Click <span className="font-semibold">Create App</span>. When asked what you want to do,
          choose <span className="font-semibold">&ldquo;Other&rdquo;</span> →{' '}
          <span className="font-semibold">&ldquo;Business&rdquo;</span>. Give the app a name (e.g.
          &ldquo;My Smorgas Social&rdquo;) and finish creating it.
        </>
      ),
    },
    {
      title: 'Add the “Facebook Login” product',
      body: (
        <>
          On the app dashboard, find <span className="font-semibold">Facebook Login</span> in the
          product list and click <span className="font-semibold">Set up</span>. You can skip the
          quickstart — you only need its settings.
        </>
      ),
    },
    {
      title: 'Register the redirect URL',
      body: (
        <>
          Go to <span className="font-semibold">Facebook Login → Settings</span> and paste this exact
          value into <span className="font-semibold">Valid OAuth Redirect URIs</span>, then{' '}
          <span className="font-semibold">Save changes</span>:
          <code
            className={cn(
              'mt-2 block break-all rounded-lg bg-chip px-3 py-2 text-xs',
              redirectUri ? 'text-ink' : 'text-ink-faint',
            )}
          >
            {redirectUri ?? 'Loading…'}
          </code>
        </>
      ),
    },
    {
      title: 'Check the permissions',
      body: (
        <>
          Open <span className="font-semibold">App Review → Permissions and Features</span>. A
          Business app gets <span className="font-semibold">Standard access</span> to every
          permission automatically, which is all you need while only people with a role on your app
          connect. If one of those below has been removed, search for it there and click{' '}
          <span className="font-semibold">Get Standard Access</span>.
          <ul className="mt-1.5 list-disc space-y-1.5 pl-5">
            <li>
              Connecting a Page asks for {scope('public_profile')}, {scope('pages_show_list')},{' '}
              {scope('pages_read_engagement')} and {scope('pages_manage_posts')}.
            </li>
            <li>
              Each optional feature you turn on later, on the Connections page, asks for more:{' '}
              {scope('pages_messaging')} for the Messenger inbox (add the{' '}
              <span className="font-semibold">Messenger</span> product first, under{' '}
              <span className="font-semibold">Add Product</span>),{' '}
              {scope('pages_read_user_content')} and {scope('pages_manage_engagement')} for
              comments &amp; mentions, and {scope('read_insights')} for Page analytics.
            </li>
          </ul>
        </>
      ),
    },
    {
      title: 'Connecting Instagram too? Check its permissions',
      body: (
        <>
          Instagram connects through this same app. Click{' '}
          <span className="font-semibold">Add Product → Instagram → Set up</span> and
          choose <span className="font-semibold">API setup with Facebook login</span>. An Instagram
          sign-in asks for all of these at once, so check each as in the step above:{' '}
          {scope('instagram_basic')}, {scope('instagram_content_publish')},{' '}
          {scope('instagram_manage_messages')}, {scope('instagram_manage_comments')},{' '}
          {scope('instagram_manage_insights')}, {scope('pages_show_list')} and{' '}
          {scope('pages_read_engagement')}. The Instagram account must be a professional account
          (Business or Creator) linked to your Facebook Page.
        </>
      ),
    },
    {
      title: 'Copy your App ID and App Secret',
      body: (
        <>
          Open <span className="font-semibold">App settings → Basic</span>. Copy the{' '}
          <span className="font-semibold">App ID</span>, then click{' '}
          <span className="font-semibold">Show</span> next to{' '}
          <span className="font-semibold">App Secret</span> and copy that too. Paste both into
          Site Integrations → Social Media Integrations →{' '}
          <span className="font-semibold">Facebook</span> in Smorgas Social (a Tenant Admin does
          this).
        </>
      ),
    },
    {
      title: 'Add yourself as a tester (while in Development)',
      body: (
        <>
          New apps start in <span className="font-semibold">Development mode</span>, so only people
          with a role on the app can connect. You (the creator) already have access. To let a
          teammate connect, add them under{' '}
          <span className="font-semibold">App roles → Roles</span> as a Tester or Admin.
        </>
      ),
    },
  ];

  return (
    // h-full + overflow-y-auto: these guides are STANDALONE routes (no AppShell), and
    // body/#root are locked to overflow:hidden so the app's inner regions own scrolling.
    // Without its own scroll region a guide taller than the viewport is simply clipped.
    <div className="h-full overflow-y-auto bg-page text-ink">
      <div className="mx-auto max-w-2xl px-5 py-10 sm:py-14">
        {/* Branding. The page follows the theme, so the logo does too: dark lettering on the light
            page, white on the dark one. The files have no size of their own, hence width and height,
            drawn in rem by the classes so the logo scales with the page (index.css). */}
        <div className="mb-8 flex items-center">
          <img
            src="/smorgas-social-logo-light.svg"
            alt="Smorgas Social"
            width={137}
            height={21}
            className="block h-[1.3125rem] w-[8.5625rem] dark:hidden"
          />
          <img
            src="/smorgas-social-logo.svg"
            alt="Smorgas Social"
            width={137}
            height={21}
            className="hidden h-[1.3125rem] w-[8.5625rem] dark:block"
          />
        </div>

        <div className="rounded-2xl border border-line bg-card p-6 shadow-card sm:p-8">
          <div className="mb-6 flex items-start gap-3">
            <span
              className={cn(
                'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl',
                PLATFORM_META[SocialPlatform.FACEBOOK].tile,
              )}
            >
              <FacebookIcon className="h-6 w-6" />
            </span>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-ink">
                Get your Facebook App ID &amp; Secret
              </h1>
              <p className="mt-1 text-sm text-ink-muted">
                Smorgas Social connects to Facebook (and to Instagram) through a{' '}
                <span className="font-medium">Meta App</span> your organization owns. A Tenant Admin
                sets it up once — it takes a few minutes — and adds two values in Site Integrations
                at the end.
              </p>
            </div>
          </div>

          <ol className="space-y-5">
            {steps.map((step, i) => (
              <li key={i} className="flex gap-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-tint text-sm font-bold text-tint-fg">
                  {i + 1}
                </span>
                <div className="pt-0.5">
                  <p className="text-sm font-semibold text-ink">{step.title}</p>
                  <div className="mt-1 text-sm leading-relaxed text-ink-label">{step.body}</div>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-7 flex gap-3 rounded-xl border border-amber-100 dark:border-warning-dark/30 bg-amber-50/70 dark:bg-warning-dark-tint/70 px-4 py-3">
            <KeyRound className="mt-0.5 h-5 w-5 shrink-0 text-amber-500 dark:text-warning-dark" aria-hidden />
            <div className="text-sm text-amber-800/90 dark:text-warning-dark">
              <p className="font-semibold text-amber-900 dark:text-warning-dark">Keep your App Secret private</p>
              <p className="mt-1">
                Treat the App Secret like a password. Smorgas Social keeps it on its server, never
                shows it again and uses it only to complete Facebook and Instagram sign-ins — but
                don&apos;t share it elsewhere or post it publicly.
              </p>
            </div>
          </div>

          <div className="mt-4 flex gap-3 rounded-xl border border-line bg-tint/70 px-4 py-3">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-tint-fg" aria-hidden />
            <div className="text-sm text-ink-strong/90">
              <p className="font-semibold text-ink">Good to know</p>
              <ul className="mt-1 list-disc space-y-1 pl-4">
                <li>
                  The redirect URL must match{' '}
                  <span className="font-semibold">exactly</span> — copy it from this page or from
                  Site Integrations.
                </li>
                <li>
                  You only need to do this once for your organization. After that, connecting a Page
                  is just a couple of clicks.
                </li>
              </ul>
            </div>
          </div>

          <GuideByoaNote />
        </div>

        <p className="mt-6 flex items-center justify-center gap-1.5 text-center text-sm text-ink-faint">
          <X className="h-4 w-4" aria-hidden /> You can close this tab and return to Smorgas Social
          when you&apos;re done.
        </p>
      </div>
    </div>
  );
}
